import { CircularProgress } from '@mui/material';
import * as React from 'react';
import { OutstandingFeesBreakdown } from 'services/clients/insights';
import { ScrollToTop } from '../ScrollToTop';
import { AnnotatedRow, HorizontalBar, LabelFormatter, Row } from './types';
import { Group } from './Group';

interface Props {
  rows: Row[];
  loading?: boolean;
  error?: boolean;
  scrollable?: boolean;
  view: OutstandingFeesBreakdown;
  showHistoric?: boolean;
  className?: string;
  labelFormatter?: LabelFormatter;
  showTooltips?: boolean;
}

export const HorizontalGraph: React.FC<Props> = (props) => {
  const {
    rows,
    loading,
    error,
    scrollable,
    view,
    showHistoric,
    className,
    labelFormatter,
    showTooltips,
  } = props;

  const scrollableRef = React.useRef<HTMLDivElement>(null);

  const annotatedRows: AnnotatedRow[] = React.useMemo(() => {
    const sums: number[] = [];
    rows.forEach((row) => {
      row.data.forEach((data) => {
        if (data.variant === 'historic' && !showHistoric) return;
        sums.push(data.blueAmount + data.redAmount);
      });
    });

    const maxValue = Math.max(...sums);

    return rows.map((row) => {
      const annotatedRow: AnnotatedRow = {
        ...row,
        bars: row.data
          .map((data) => {
            if (data.variant === 'historic' && !showHistoric) return null;
            const total = data.blueAmount + data.redAmount;
            const horizontalBar: HorizontalBar = {
              normalizedLength: total / maxValue,
              blueProportion: data.blueAmount / total,
              redProportion: data.redAmount / total,
              variant: data.variant,
              detail: data.detail,
            };
            return horizontalBar;
          })
          .filter((r) => r !== null) as HorizontalBar[],
      };
      return annotatedRow;
    });
  }, [rows, showHistoric]);

  const entries = React.useMemo(() => {
    return annotatedRows.map((row) => {
      return (
        <Group
          key={row.key}
          row={row}
          labelFormatter={labelFormatter}
          view={view}
          showTooltips={showTooltips}
        />
      );
    });
  }, [annotatedRows, view, labelFormatter, showTooltips]);

  let inner: React.ReactNode = <h5 className="text-gray-500">No data</h5>;
  if (loading) {
    inner = null;
  } else if (error) {
    inner = <h1 className="text-red-900 font-bold">Unavailable</h1>;
  }

  const empty = (
    <div className="w-full h-full flex items-center justify-center text-center py-10">{inner}</div>
  );

  return (
    <div className="relative">
      <div
        className={`overflow-y-auto ${
          scrollable && entries.length ? 'scrollbar pb-6 pr-1' : ''
        } ${className}`}
        ref={scrollableRef}
      >
        {entries.length > 0 ? entries : empty}
        {scrollable && <ScrollToTop element={scrollableRef} />}
        {loading && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <CircularProgress />
          </div>
        )}
      </div>
    </div>
  );
};
